/** @jsx jsx */
import { useEffect, useState, useMemo } from 'react'
import { jsx, Flex, Heading, Box, Image, Text } from 'theme-ui'
import { useLocation } from 'react-use'

import { space } from '~/gatsby-plugin-theme-ui'

import { ProductItemComponentType } from '~/@types/models'

import ProductItemVariantsList, {
  TooltipImageLayout,
} from '~/components/product-item-variants-list'

import { mapColorOptionsWithAvailability } from '../parameters/util'

import { ProductItemsProps, ImageSize, TitleNotePosition, imageSizeMap } from './types'

import ImageWrapper from '~/components/image-wrapper'
import { Link } from '~/componentsV2/linkPrev/LinkPrev'

import { ProductLabel, ProductLabelGroup } from '~/components/product-label'

import { useProductLink } from './hook'
import useProductVariantInfo from '~/hooks/use-product-variant-info'

import {
  addScaleClasses,
  isNoScale,
  imageClassByType,
  scaleImageStyles,
  isRugProduct,
} from '~/utils/product'

const labelVariants = {
  newArrival: {
    name: 'New',
    color: '#001f39',
  },
}

const getLabels = (product: ProductItemComponentType) => {
  if (!product.labels) {
    return []
  }

  const labelsArray = []
  for (const [labelName, value] of Object.entries(product.labels)) {
    if (
      value &&
      labelVariants[labelName as keyof typeof labelVariants] &&
      labelName !== 'newArrival'
    ) {
      labelsArray.push(labelVariants[labelName as keyof typeof labelVariants])
    }
  }
  return labelsArray
}

const ProductItem = ({
  variant = 'primary',
  product,
  visibleVariantsCount,
  productImageRatio = 1,
  imageSize = ImageSize.FULLSCREEN,
  titleNotePosition = TitleNotePosition.BELOW_TITLE,
  productVariant,
  collectionHandle,
  isNoScaleCustom = false,
  showPrice,
  onlyCompareAtPriceVariants,
}: ProductItemsProps) => {
  const variantsWithAvailability = mapColorOptionsWithAvailability(
    product,
    product.colorOptions || product.colorOptionsFinish,
    [],
    onlyCompareAtPriceVariants
  )

  const variants = variantsWithAvailability?.filter(v => !v.hideColor)

  const currentVariant = useMemo(() => {
    let currentVariant

    if (variants && onlyCompareAtPriceVariants) {
      const [current] = product.variants.filter(variant => {
        const [firstVariant] = variants
        const [firstSelectedOption] = variant?.selectedOptions

        const currentColor = firstVariant?.title
        return firstSelectedOption.value === currentColor
      })

      currentVariant = current
    }

    return currentVariant
  }, [variants, onlyCompareAtPriceVariants, product.variants])

  const { rangePrice } = useProductVariantInfo({
    product,
    isStartingAt: true,
    productVariant,
  })

  const { linkProps, productLink } = useProductLink({
    product,
    currentVariant,
    collectionHandle,
  })

  const [imageFromVariants, setImageFromVariants] = useState(product.mainImage)

  const hasColorsOptions =
    (product.colorOptions && product.colorOptions.length > 0) ||
    (product.colorOptionsFinish && product.colorOptionsFinish.length > 0)
  const hasVariantImages = product.variantImages && product.variantImages.length > 0

  const showColorNote = (type: TitleNotePosition) => {
    return titleNotePosition === type && product.colorName
  }

  const labels = getLabels(product)

  useEffect(() => {
    if (currentVariant) {
      const { altText, id, ...imageRest } = currentVariant.image

      setImageFromVariants({
        id,
        ...imageRest,
      })
    }
  }, [currentVariant])

  const applyScale = !isNoScale(product) && !isNoScaleCustom

  const { pathname } = useLocation()

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {imageFromVariants && (
        <Link
          to={productLink}
          state={{ prevPath: pathname }}
          sx={applyScale ? scaleImageStyles : null}
          {...linkProps}
          className={`${applyScale ? addScaleClasses(product) : ``}`}
        >
          <ImageWrapper ratio={productImageRatio} center>
            <Image
              className={`${imageClassByType(product)} ${product.handle}`}
              src={imageFromVariants.srcDesktopMedium}
              loading="lazy"
              variant={imageSizeMap[imageSize]}
              alt={product.title || 'Product Image'}
              onMouseOver={(e: { currentTarget: { src: string } }) => {
                pathname !== '/search' &&
                  product.images[1] &&
                  (e.currentTarget.src = product.images[1].srcDesktopMedium)
              }}
              onMouseOut={(e: { currentTarget: { src: string } }) => {
                e.currentTarget.src = imageFromVariants.srcDesktopMedium
              }}
            />
            {/* Removed on BSOLTS-37-shown-in-issue */}
            {/* {showColorNote(TitleNotePosition.IMAGE_BOTTOM) && (
              <Text
                as="p"
                variant="small"
                sx={{
                  position: 'absolute',
                  bottom: [space.sm],
                  right: [space.md1],
                  color: 'txt',
                }}
              >
                Shown in {product.colorName}
              </Text>
            )} */}
          </ImageWrapper>
          {labels && labels.length > 0 && (
            <ProductLabelGroup>
              {labels.map(label => (
                <ProductLabel key={label.name} label={label.name} color={label.color} />
              ))}
            </ProductLabelGroup>
          )}
        </Link>
      )}

      <Flex
        as="footer"
        sx={{
          px: ['0', '24px'],
          flexDirection: 'column',
          mt: ['10px', space.gridGap3],
          mb: ['0', space.gridGap2],
        }}
      >
        <Box
          sx={{
            textAlign: ['left', 'left'],
            width: ['100%', 'initial'],
          }}
        >
          <Link
            to={productLink}
            sx={{
              textDecoration: 'none',
            }}
            {...linkProps}
          >
            <Heading
              as="h3"
              sx={{
                variant: `variants.product.${variant}.title`,
                lineHeight: 1.15,
                textTransform: 'uppercase',
                fontSize: ['12px', '22px'],
                fontWeight: 400,
                color: ['#2b2b2b'],
                marginBottom: ['5px', '15px'],
              }}
            >
              {product.title}
            </Heading>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: ['column', 'row'],
                flexWrap: 'wrap',
              }}
            >
              {hasColorsOptions && (
                <ProductItemVariantsList
                  inStockColorOption={
                    product?.colorOptions?.length > 4 ? 4 : product?.colorOptions?.length
                  }
                  variants={variants}
                  visibleVariantsCount={variants?.length > 4 ? 4 : variants?.length}
                  {...(isRugProduct(product) && {
                    tooltipLayout: TooltipImageLayout.COVER,
                  })}
                />
              )}
              {hasVariantImages && (
                <ProductItemVariantsList
                  variants={product.variantImages}
                  visibleVariantsCount={
                    product?.variantImages?.length > 4 ? 4 : product.variantImages.length
                  }
                  {...(isRugProduct(product) && {
                    tooltipLayout: TooltipImageLayout.COVER,
                  })}
                />
              )}
              {showPrice && <Box>{rangePrice}</Box>}
            </Box>
          </Link>
        </Box>
      </Flex>
    </Flex>
  ) as JSX.Element
}
export default ProductItem
